import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output
} from '@angular/core';
import { Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pagination-tile',
  templateUrl: './pagination-tile.component.html',
  styleUrls: ['./pagination-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginationTileComponent implements OnInit, OnChanges {
  @Input() totalSize: number;
  @Input() perPage: number;
  @Input() pageCount: number;
  @Input() maxSize: number;
  @Input() currentPage: number;
  @Output() selectedPage = new EventEmitter<number>();

  pagesList: number[] = [];
  showFirstSection = false;
  showLastSection = false;

  calculateListOfPages({ start, end }: { start: number; end: number }): void {
    this.pagesList.length = 0;

    if (this.pageCount < this.maxSize) {
      start = 1;
      end = this.maxSize;
    }

    for (let i = start; i <= end; i++) {
      if (i <= this.pageCount) {
        this.pagesList.push(i);
      }
    }

    this.showFirstPoints(start);
    this.showLastPoints(end);
  }

  private showFirstPoints(start): void {
    if (start >= 3) {
      this.showFirstSection = true;
    } else {
      this.showFirstSection = false;
      if (start === 2) {
        this.pagesList.unshift(1);
      }
    }
  }

  private showLastPoints(end): void {
    const total = this.pageCount - 2;
    this.showLastSection = end <= total;

    if (end === this.pageCount - 1) {
      this.pagesList.push(this.pageCount);
    }
  }

  initPagesList(): Observable<{ start: number; end: number }> {
    let start = this.currentPage;
    let end = this.maxSize;

    if (this.currentPage > 1) {
      start = this.currentPage - 1;
      end = start + this.maxSize - 1;
    }

    if (this.currentPage === this.pageCount && this.pageCount >= this.maxSize + 2) {
      start = this.pageCount - this.maxSize + 1;
      end = this.pageCount;
    }

    return of({
      start,
      end
    });
  }

  selectPage(page: number, e): void | boolean {
    e.stopPropagation();
    e.preventDefault();

    if (page === this.currentPage) {
      return false;
    }

    this.initPages();
    this._cdr.detectChanges();
    this.selectedPage.next(page);
  }

  goPrevPage(e): void | boolean {
    e.stopPropagation();
    e.preventDefault();

    if (this.currentPage === 1) {
      return false;
    }
    this.currentPage = this.currentPage - 1;

    this.initPages();
    this._cdr.detectChanges();
    this.selectedPage.next(this.currentPage);
  }

  goNextPage(e): void | boolean {
    e.stopPropagation();
    e.preventDefault();

    if (this.currentPage === this.pageCount) {
      return false;
    }
    this.currentPage = this.currentPage + 1;

    this.initPages();
    this._cdr.detectChanges();
    this.selectedPage.next(this.currentPage);
  }

  private initPages(): void {
    this.initPagesList()
      .pipe(map((val) => this.calculateListOfPages(val)))
      .subscribe();
  }

  ngOnInit(): void {
    this.initPages();
  }

  ngOnChanges(): void {
    this.initPages();
  }

  constructor(private _cdr: ChangeDetectorRef) {}
}
